import { Core } from "@faire/design-tokens";
import {
  StyledText,
  StyledPill,
  LoadingTextPlaceholder,
  Wrapper,
} from "@faire/retailer-visitor-shared/components/Filters/__internal__/PillHelpers";
import { handleKeyDown } from "@faire/retailer-visitor-shared/lib/handleKeyDown";
import { getSettingElevateTopShopV2 } from "@faire/retailer-visitor-shared/settings/getSettingElevateTopShopV2";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { DefaultTheme } from "@faire/web--source/slate/Theme";
import { ThemeProvider } from "@faire/web--source/slate/Theme/ThemeProvider";
import { ISearchFilterPill } from "@faire/web-api--source/indigofair/data/ISearchFilterPill";
import React, { useCallback, useMemo } from "react";

export interface IProps {
  filterPill: ISearchFilterPill;
  onChange: (eventId: string, newQuery: string, refType: string) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onTouchStart?: () => void;
  showLoading?: boolean;
  disabled?: boolean;
}

export const SearchSuggestionPill: React.FC<IProps> = ({
  filterPill,
  onChange,
  onMouseEnter,
  onMouseLeave,
  onTouchStart,
  showLoading = false,
  disabled = false,
}) => {
  const { query_suggestion } = filterPill;
  const { strictLocalize } = useStrictLocalization();
  const isInvisible = query_suggestion?.is_invisible ?? false;
  const queryTerm = query_suggestion?.query_term ?? "";
  const displayName = query_suggestion?.display_name ?? "";
  const eventId = query_suggestion?.event_id ?? "";
  const refType = query_suggestion?.ref_type ?? "";

  const onClick = useCallback(() => {
    if (!disabled) {
      onChange(eventId, queryTerm, refType);
    }
  }, [disabled, onChange, eventId, queryTerm, refType]);

  const pillStyles = useMemo((): React.CSSProperties => {
    const styles: React.CSSProperties = {};
    if (showLoading) {
      styles.backgroundColor = Core.surface.subdued;
    } else {
      styles.border = `1px solid ${Core.border.muted}`;
      styles.backgroundColor = Core.surface.inverse;
    }
    if (disabled) {
      styles.backgroundColor = Core.surface.subdued;
      styles.cursor = `not-allowed`;
    }
    return styles;
  }, [disabled, showLoading]);

  const hoverColor = useMemo((): string | undefined => {
    if (!showLoading && !disabled) {
      return Core.border.default;
    }
    return undefined;
  }, [disabled, showLoading]);

  if (!showLoading && query_suggestion?.query_term === undefined) {
    return null;
  }

  return (
    <div className={`${isInvisible ? "hidden" : "mr-2"}`}>
      <Wrapper
        role="button"
        aria-pressed={false}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onKeyDown={handleKeyDown(onClick)}
        tabIndex={disabled ? -1 : 0}
        aria-label={
          displayName ??
          strictLocalize({
            defaultMessage: "Loading",
            description: {
              text: "Waiting for the data to be loaded",
            },
          })
        }
        data-test-id={`filter-toggle-wrapper-${displayName}`}
        style={{
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {!getSettingElevateTopShopV2() ? null : (
          <ThemeProvider theme={DefaultTheme}>
            <StyledPill
              isOpen={false}
              alwaysShowCloseButton={false}
              hasFilterSelected={false}
              style={pillStyles}
              $hoverColor={hoverColor}
            >
              {showLoading ? <LoadingTextPlaceholder /> : null}
              <StyledText
                id={`toggle-${queryTerm}`}
                variant="paragraphSansRegular"
                color={Core.text.default}
              >
                {displayName}
              </StyledText>
            </StyledPill>
          </ThemeProvider>
        )}
      </Wrapper>
    </div>
  );
};
